import { useQuery } from '@apollo/client/react';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { Fragment, useState } from 'react';
import BookingCreateForm from '../../booking/components/BookingCreateForm';
import CleaningCard from '../../cleaning/component/CleaningCard';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import formatMoney from '../../core/formatMoney';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import assert from '../../core/util/assert';
import BookingCard from '../../main/component/BookingCard';
import {
    homeQuery,
    HomeQueryResult,
    HomeQueryResultBooking,
} from '../../main/queries';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import useProperty from '../../property/hooks/useProperty';

const averageCardHeight = 270;
const cleaningCardHeight = 65;

const Home = () => {
    const propertyId = usePropertyId();
    const { isCleaningEnabled } = useProperty();
    const cleaningEnabled = isCleaningEnabled();
    const { loading, error, data } = useQuery<HomeQueryResult>(homeQuery, {
        pollInterval: 3600,
        variables: { propertyId: propertyId ?? 0 },
        skip: !propertyId,
    });
    const [showBookingForm, setShowBookingForm] = useState(false);

    const pathname = window.location.hash.replace('#', '');

    // Only show property selection if we're on the root path (/) or a property home path (/:propertyId/home)
    // Don't interfere with other routes like /properties, /users, /settings
    const isRootPath = pathname === '/' || pathname === '';
    const isPropertyHomePath = /^\/\d+\/home/.test(pathname);

    if (!propertyId && !isRootPath && !isPropertyHomePath) {
        // We're on some other route (like /properties) that doesn't need a propertyId
        // Don't render anything - let the router handle it
        return null;
    }

    // Show property selection if no property ID in URL on root or home paths
    if (!propertyId) {
        return <PropertySelectionDialog open={true} targetPath="" />;
    }

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" gap={3}>
                <Skeleton variant="text" height={50} />
                {new Array(6).fill(0).map((_, index) => (
                    <Fragment key={index} /* NOSONAR */>
                        <Skeleton
                            variant="rectangular"
                            height={averageCardHeight}
                        />
                        <Skeleton
                            variant="rectangular"
                            height={cleaningCardHeight}
                        />
                    </Fragment>
                ))}
            </Box>
        );
    }

    if (error) return <ErrorBox error={error} />;

    assert(data);

    const {
        home: { bookings },
        cleaningList,
    } = data;

    const relevantBookings = bookings
        .slice()
        .filter(
            booking =>
                booking.status !== 'canceled' &&
                (!booking.detailsRecorded ||
                    !booking.hasInstructions ||
                    !booking.invoiceId ||
                    booking.status !== 'complete')
        )
        .sort((a, b) => a.checkin.localeCompare(b.checkin));

    const months = relevantBookings.reduce((a, b) => {
        const month = b.checkin.substring(0, 7);
        if (!a.includes(month)) {
            return [...a, month];
        }
        return a;
    }, [] as string[]);

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    p: 2,
                    backgroundColor: '#1a1a2e',
                    border: '3px solid #6366f1',
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Bookings
                </Typography>
                <ResponsiveActionButton
                    label="New Booking"
                    icon={<AddIcon />}
                    onClick={() => setShowBookingForm(true)}
                />
            </Box>
            {months.map(month => {
                const bookingsForThisMonth = relevantBookings.filter(booking =>
                    booking.checkin.startsWith(month)
                );

                const firstBookingDateForThisMonth =
                    bookingsForThisMonth[0].checkin;

                const cleaningsForThisMonth = cleaningEnabled
                    ? cleaningList
                          .filter(cleaning => cleaning.time.startsWith(month))
                          .filter(
                              cleaning =>
                                  cleaning.time.substring(0, 10) >=
                                      firstBookingDateForThisMonth ||
                                  !cleaning.paid
                          )
                    : [];

                const itemsForThisMonth: (
                    | [string, 'booking', HomeQueryResultBooking]
                    | [
                          string,
                          'cleaning',
                          {
                              id: string;
                              time: string;
                              note: string;
                              value: number;
                              paid: boolean;
                          },
                      ]
                    | [string, 'cleaningPlaceholder']
                )[] = [
                    ...bookingsForThisMonth.map(
                        item =>
                            [item.checkin, 'booking', item] as [
                                string,
                                'booking',
                                typeof item,
                            ]
                    ),
                    ...cleaningsForThisMonth.map(
                        item =>
                            [item.time.substring(0, 10), 'cleaning', item] as [
                                string,
                                'cleaning',
                                typeof item,
                            ]
                    ),
                ];

                itemsForThisMonth.sort(([a], [b]) => a.localeCompare(b));

                if (cleaningEnabled) {
                    for (let i = 0; i < itemsForThisMonth.length - 1; i++) {
                        const current = itemsForThisMonth[i];
                        const next = itemsForThisMonth[i + 1];

                        if (current[1] === 'booking' && next[1] === 'booking') {
                            itemsForThisMonth.splice(i + 1, 0, [
                                `${current[2].checkout} ${current[2].checkinTime}`,
                                'cleaningPlaceholder',
                            ]);
                        }
                    }
                }

                return (
                    <Fragment key={month}>
                        {month.endsWith('01') && (
                            <h2>{month.substring(0, 4)}</h2>
                        )}
                        <h3>
                            {DateTime.fromSQL(`${month}-01`).toFormat('LLLL')}{' '}
                            {formatMoney(
                                bookingsForThisMonth.reduce(
                                    (a, b) => a + b.revenue,
                                    0
                                ),
                                'HUF'
                            )}
                        </h3>
                        {itemsForThisMonth.map(current => {
                            if (current[1] === 'booking') {
                                return (
                                    <BookingCard
                                        booking={current[2]}
                                        key={current[2].reference}
                                    />
                                );
                            }
                            if (current[1] === 'cleaning') {
                                const [, , cleaning] = current;
                                return (
                                    <CleaningCard
                                        key={cleaning.id}
                                        cleaning={cleaning}
                                    />
                                );
                            }
                            if (current[1] === 'cleaningPlaceholder') {
                                const [dateTime] = current;
                                return (
                                    <CleaningCard
                                        key={`cleaningPlaceholder${dateTime}`}
                                        suggestedTime={dateTime}
                                    />
                                );
                            }
                            return null;
                        })}
                    </Fragment>
                );
            })}
            <BookingCreateForm
                open={showBookingForm}
                onClose={() => setShowBookingForm(false)}
            />
        </PageContainer>
    );
};

export default Home;
